import { useMemo, useCallback, useState } from 'react'
import type { LEDProcessor, LEDPanel, PowerCabinet } from '@/types/synoptique'

/** Normalise une chaîne pour la recherche : minuscules + suppression des accents/diacritiques */
function normalize(str: string): string {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

/** Nettoie les artefacts d'encodage CSV dans les noms de marques (ex: $N$, $$ ) */
export function cleanBrand(brand: string): string {
  return brand
    .replace(/\$[A-Za-z]\$/g, '')  // supprime $N$, $S$, etc.
    .replace(/\$+/g, '')            // supprime les $ restants
    .trim()
}

/** Chiffres romains (multi-caractères) vers arabes, triés du plus long au plus court */
const ROMAN_ARABIC_MAP: [string, string][] = [
  ['viii', '8'], ['xiii', '13'], ['xii', '12'],
  ['vii', '7'], ['iii', '3'], ['xi', '11'],
  ['vi', '6'], ['iv', '4'], ['ix', '9'], ['ii', '2'],
]

/** Remplace les chiffres romains (mots complets) par leurs équivalents arabes */
function romanToArabic(str: string): string {
  let result = str
  for (const [roman, arabic] of ROMAN_ARABIC_MAP) {
    result = result.replace(new RegExp(`\\b${roman}\\b`, 'g'), arabic)
  }
  return result
}

/** Remplace les nombres arabes (2-13, mots complets) par leurs équivalents romains */
function arabicToRoman(str: string): string {
  return str.replace(/\b(\d{1,2})\b/g, (match) => {
    const entry = ROMAN_ARABIC_MAP.find(([, arabic]) => arabic === match)
    return entry ? entry[0] : match
  })
}

/** Insère des espaces aux transitions lettre↔chiffre pour segmenter les termes collés (ex: "upad4s" → "upad 4 s") */
function expandTransitions(str: string): string {
  return str
    .replace(/([a-z])(\d)/g, '$1 $2')
    .replace(/(\d)([a-z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Génère des variantes de recherche : original, compact, expanded (transitions segmentées), roman↔arabe sur chaque forme */
function searchVariants(str: string): string[] {
  const base = normalize(str).trim()
  if (!base) return []
  const compact = base.replace(/[-_.\s]+/g, '')
  // Normaliser les tirets en espaces puis segmenter les transitions lettre↔chiffre
  const expanded = expandTransitions(base.replace(/[-_]+/g, ' '))
  const variants = [base, compact]
  if (expanded !== base) {
    variants.push(expanded, expanded.replace(/\s+/g, ''))
  }

  // Appliquer roman↔arabe sur base ET expanded (couvre "upad4 s" → expand → "upad 4 s" → "upad iv s")
  for (const source of [base, expanded]) {
    const withRoman = arabicToRoman(source)
    if (withRoman !== source) {
      variants.push(withRoman, withRoman.replace(/[-_.\s]+/g, ''))
    }
    const withArabic = romanToArabic(source)
    if (withArabic !== source) {
      variants.push(withArabic, withArabic.replace(/[-_.\s]+/g, ''))
    }
  }

  return [...new Set(variants)]
}

/** Vérifie si le texte cible contient l'une des variantes de recherche (normal + compact) */
function smartIncludes(target: string, queryVariants: string[]): boolean {
  const normalizedTarget = normalize(target)
  const compactTarget = normalizedTarget.replace(/[-_.\s]+/g, '')
  return queryVariants.some(q => normalizedTarget.includes(q) || compactTarget.includes(q))
}

interface UseFilteredCatalogParams {
  processors: LEDProcessor[]
  panels: LEDPanel[]
  powerCabinets: PowerCabinet[]
  debouncedSearchProcessor: string
  debouncedSearchPanel: string
  debouncedSearchCabinet: string
}

export function useFilteredCatalog({
  processors,
  panels,
  powerCabinets,
  debouncedSearchProcessor,
  debouncedSearchPanel,
  debouncedSearchCabinet,
}: UseFilteredCatalogParams) {
  // États pour le collapse/expand des marques
  const [collapsedProcessorBrands, setCollapsedProcessorBrands] = useState<Set<string>>(new Set())
  const [collapsedPanelBrands, setCollapsedPanelBrands] = useState<Set<string>>(new Set())
  const [collapsedCabinetBrands, setCollapsedCabinetBrands] = useState<Set<string>>(new Set())

  // Filtrer les processeurs par recherche - OPTIMISÉ avec useMemo + debounce
  // ✅ Utilise debouncedSearchProcessor au lieu de searchProcessor
  const filteredProcessors = useMemo(() => {
    if (!normalize(debouncedSearchProcessor).trim()) return processors
    const qv = searchVariants(debouncedSearchProcessor)
    return processors.filter(proc =>
      smartIncludes(proc.name, qv) ||
      smartIncludes(proc.brand, qv) ||
      smartIncludes(proc.model, qv) ||
      smartIncludes(`${proc.brand} ${proc.model}`, qv) ||
      smartIncludes(`${proc.brand} ${proc.name}`, qv)
    )
  }, [processors, debouncedSearchProcessor])

  // Regrouper les processeurs par marque - OPTIMISÉ avec useMemo
  const processorsByBrand = useMemo(() => {
    return filteredProcessors.reduce((acc, processor) => {
      if (!acc[processor.brand]) {
        acc[processor.brand] = []
      }
      acc[processor.brand].push(processor)
      return acc
    }, {} as Record<string, LEDProcessor[]>)
  }, [filteredProcessors])

  // Obtenir les marques triées alphabétiquement - OPTIMISÉ avec useMemo
  const sortedBrands = useMemo(() =>
    Object.keys(processorsByBrand).sort(),
    [processorsByBrand]
  )

  // Filtrer les dalles par recherche - OPTIMISÉ avec useMemo + debounce
  // ✅ Supporte : nom, marque, pitch (ex: "2.6"), marque+pitch (ex: "ROE 2.6"),
  //    résolution pixels (ex: "192x192"), marque+résolution (ex: "ROE 192x192"),
  //    nom+pitch en suffixe (ex: "Upad 4 2.6" → nom "Upad 4" + pitch 2.6, inclut variante "Upad IV 2.6")
  const filteredPanels = useMemo(() => {
    const query = normalize(debouncedSearchPanel).trim()
    if (!query) return panels

    const parts = query.split(/\s+/)
    const resolutionPattern = /^(\d+)\s*x\s*(\d+)$/i
    const pitchPattern = /^\d+([.,]\d+)?$/
    // Un pitch décimal en suffixe : accepte "2.6", "2." (tous les 2.X), "1,9", "3." etc.
    const decimalPitchPattern = /^\d+[.,]\d*$/

    // Détecter un pitch décimal en dernière position de la requête (ex: "Upad 4 2.6" → nameQuery="Upad 4", pitchPrefix="2.6")
    // Comparaison par PRÉFIXE : "2.6" matche 2.6, 2.60, 2.604 ; "2." matche tous les 2.X
    let pitchPrefix: string | null = null
    let nameQuery = debouncedSearchPanel
    if (parts.length >= 2 && decimalPitchPattern.test(parts[parts.length - 1])) {
      pitchPrefix = parts[parts.length - 1].replace(',', '.')
      nameQuery = parts.slice(0, -1).join(' ')
    }

    // Variantes de recherche : si pitch isolé, on les calcule uniquement sur la partie nom
    const qv = searchVariants(nameQuery)

    return panels.filter(panel => {
      const pitchStr = String(panel.pixelPitch)
      const pitchStrComma = pitchStr.replace('.', ',')
      const resStr = `${panel.resolution.width}x${panel.resolution.height}`

      // Recherche nom + pitch suffixe (ex: "Upad 4 2.6" → Upad IV 2.6 / 2.604 aussi)
      if (pitchPrefix !== null) {
        // toFixed(3) garantit la représentation décimale ("2.0" pas "2", "2.604" pas "2.60")
        if (!panel.pixelPitch.toFixed(3).startsWith(pitchPrefix)) return false
        return (
          smartIncludes(panel.name, qv) ||
          smartIncludes(panel.brand, qv) ||
          smartIncludes(`${panel.brand} ${panel.name}`, qv)
        )
      }

      // Recherche combinée marque + pitch ou marque + résolution (ex: "ROE 2.6", "ROE 192x192")
      if (parts.length >= 2) {
        const brandPart = parts[0]
        const valuePart = parts.slice(1).join(' ')
        const brandMatch = normalize(panel.brand).includes(brandPart)

        if (brandMatch) {
          const normalizedValue = valuePart.replace(',', '.')
          if (pitchPattern.test(valuePart) && pitchStr === normalizedValue) return true
          const resMatch = valuePart.replace(/\s/g, '').match(resolutionPattern)
          if (resMatch && panel.resolution.width === Number(resMatch[1]) && panel.resolution.height === Number(resMatch[2])) return true
        }
      }

      // Recherche par pitch seul (ex: "2.6" ou "2,6")
      const normalizedQuery = query.replace(',', '.')
      if (pitchPattern.test(query) && pitchStr === normalizedQuery) return true

      // Recherche par résolution seule (ex: "192x192")
      const queryResMatch = query.replace(/\s/g, '').match(resolutionPattern)
      if (queryResMatch && panel.resolution.width === Number(queryResMatch[1]) && panel.resolution.height === Number(queryResMatch[2])) return true

      // Recherche intelligente : nom/marque avec roman↔arabe et espace-insensible
      return smartIncludes(panel.name, qv) ||
        smartIncludes(panel.brand, qv) ||
        smartIncludes(`${panel.brand} ${panel.name}`, qv) ||
        pitchStr.includes(query) ||
        pitchStrComma.includes(query) ||
        resStr.includes(query)
    })
  }, [panels, debouncedSearchPanel])

  // Regrouper les dalles par marque - OPTIMISÉ avec useMemo
  const panelsByBrand = useMemo(() => {
    return filteredPanels.reduce((acc, panel) => {
      const key = cleanBrand(panel.brand)
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(panel)
      return acc
    }, {} as Record<string, LEDPanel[]>)
  }, [filteredPanels])

  // Obtenir les marques de dalles triées alphabétiquement - OPTIMISÉ avec useMemo
  const sortedPanelBrands = useMemo(() =>
    Object.keys(panelsByBrand).sort(),
    [panelsByBrand]
  )

  // Filtrer les armoires/coffrets par recherche - OPTIMISÉ avec useMemo + debounce
  // ✅ Utilise debouncedSearchCabinet au lieu de searchCabinet
  const filteredCabinets = useMemo(() => {
    if (!normalize(debouncedSearchCabinet).trim()) return powerCabinets
    const qv = searchVariants(debouncedSearchCabinet)
    return powerCabinets.filter(cabinet =>
      smartIncludes(cabinet.name, qv) ||
      smartIncludes(cabinet.brand, qv) ||
      smartIncludes(cabinet.type, qv) ||
      smartIncludes(`${cabinet.brand} ${cabinet.name}`, qv)
    )
  }, [powerCabinets, debouncedSearchCabinet])

  // Regrouper les armoires par marque - OPTIMISÉ avec useMemo
  const cabinetsByBrand = useMemo(() => {
    return filteredCabinets.reduce((acc, cabinet) => {
      if (!acc[cabinet.brand]) {
        acc[cabinet.brand] = []
      }
      acc[cabinet.brand].push(cabinet)
      return acc
    }, {} as Record<string, PowerCabinet[]>)
  }, [filteredCabinets])

  // Obtenir les marques d'armoires triées alphabétiquement - OPTIMISÉ avec useMemo
  const sortedCabinetBrands = useMemo(() =>
    Object.keys(cabinetsByBrand).sort(),
    [cabinetsByBrand]
  )

  // Fonctions pour gérer le collapse/expand des marques - OPTIMISÉ avec useCallback
  const toggleProcessorBrandCollapse = useCallback((brand: string) => {
    setCollapsedProcessorBrands(prev => {
      const newSet = new Set(prev)
      if (newSet.has(brand)) {
        newSet.delete(brand)
      } else {
        newSet.add(brand)
      }
      return newSet
    })
  }, [])

  const togglePanelBrandCollapse = useCallback((brand: string) => {
    setCollapsedPanelBrands(prev => {
      const newSet = new Set(prev)
      if (newSet.has(brand)) {
        newSet.delete(brand)
      } else {
        newSet.add(brand)
      }
      return newSet
    })
  }, [])

  const toggleCabinetBrandCollapse = useCallback((brand: string) => {
    setCollapsedCabinetBrands(prev => {
      const newSet = new Set(prev)
      if (newSet.has(brand)) {
        newSet.delete(brand)
      } else {
        newSet.add(brand)
      }
      return newSet
    })
  }, [])

  return {
    // Filtered data
    filteredProcessors,
    processorsByBrand,
    sortedBrands,
    filteredPanels,
    panelsByBrand,
    sortedPanelBrands,
    filteredCabinets,
    cabinetsByBrand,
    sortedCabinetBrands,
    // Collapse state
    collapsedProcessorBrands,
    collapsedPanelBrands,
    collapsedCabinetBrands,
    // Collapse setters
    setCollapsedProcessorBrands,
    setCollapsedPanelBrands,
    setCollapsedCabinetBrands,
    // Collapse toggles
    toggleProcessorBrandCollapse,
    togglePanelBrandCollapse,
    toggleCabinetBrandCollapse,
  }
}
